import { Grid, List, makeStyles, Typography } from "@material-ui/core";
import { useQuery } from "@redux-requests/react";
import { groupBy, orderBy } from "lodash";
import moment from "moment";
import React, { useEffect } from "react";
import { useIntl } from "react-intl";
import { useDispatch } from "react-redux";
import { BaseLayout, Header, Loader, NoData } from "../components";
import MeetingListItem from "../components/MeetingListItem";
import { deleteFavourite, deleteMeeting, getFavouriteEvents, getMeetings } from "../ducks/actions";
import * as types from "../ducks/types";
import { EventListItem } from "../features/agenda";
import { useActionRequest, useUserMe } from "../hooks";

const useStyles = makeStyles(theme => ({
  root: {
    "& > *": {
      marginBottom: theme.spacing(3)
    }
  },
  eventContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(1),
    margin: theme.spacing(1)
  },
  textDisabled: {
    "& > *": {
      color: "grey",
      textDecoration: "line-through"
    }
  }
}));

const AgendaPage = () => {
  const classes = useStyles();
  const intl = useIntl();
  const dispatch = useDispatch();
  const { user } = useUserMe();

  useEffect(() => {
    dispatch(getFavouriteEvents());
    dispatch(getMeetings());
  }, [dispatch]);

  const deleteFavouriteRequest = useActionRequest({
    action: deleteFavourite
  });

  const onDeleteFavourite =
    ({ id }) =>
    () => {
      deleteFavouriteRequest({ id });
    };
  const {
    data: { data: favouriteEvents },
    loading: favouriteLoading
  } = useQuery({ type: types.GET_FAVOURITE_EVENTS, defaultData: { data: [] } });

  const favouriteEventsByDay = groupBy(favouriteEvents, ({ favouritable }) =>
    moment(favouritable?.startAt).format("DD/MM/YYYY")
  );

  const deleteMeetingRequest = useActionRequest({
    action: deleteMeeting
  });

  const onDeleteMeeting =
    ({ id }) =>
    () => {
      deleteMeetingRequest({ id });
    };

  const {
    data: { data: meetings },
    loading: meetingsLoading
  } = useQuery({ type: types.GET_MEETINGS, defaultData: { data: [] } });

  const meetingsByDay = groupBy(meetings, meeting =>
    moment(meeting.meetingSlot.startAt).format("DD/MM/YYYY")
  );

  return (
    <BaseLayout className={classes.root}>
      <Header
        icon="timetable"
        title={intl.formatMessage({ id: "Pages.AgendaPage.Agenda", defaultMessage: "Agenda" })}
      />
      <Loader loading={favouriteLoading}>
        <Typography variant="h6" gutterBottom>
          <strong>
            {intl.formatMessage({
              id: "Pages.AgendaPage.Events",
              defaultMessage: "Events"
            })}
          </strong>
        </Typography>
        <NoData
          message={intl.formatMessage({
            id: "Pages.AgendaPage.NoRecordingData",
            defaultMessage: "There are no recordings. Please check later for new contents..."
          })}
          data={favouriteEventsByDay}
        >
          <Grid container spacing={1}>
            {orderBy(
              Object.keys(favouriteEventsByDay),
              day => moment(day, "DD/MM/YYYY"),
              "asc"
            ).map(day => (
              <>
                <Grid item xs={12} sm={3} md={2} key={day}>
                  <Typography variant="h6">{day}</Typography>
                </Grid>
                <Grid item xs={12} sm={9} md={10}>
                  <List>
                    {orderBy(
                      favouriteEventsByDay[day],
                      ({ favouritable: programEvent }) => programEvent?.startAt
                    ).map(({ id, favouritable: programEvent }) => {
                      return (
                        <EventListItem
                          programEvent={programEvent}
                          onDelete={() => onDeleteFavourite({ id })}
                        />
                      );
                    })}
                  </List>
                </Grid>
              </>
            ))}
          </Grid>
        </NoData>
      </Loader>
      <Loader loading={meetingsLoading}>
        <Typography variant="h6" gutterBottom>
          <strong>
            {intl.formatMessage({
              id: "Pages.AgendaPage.Meetings",
              defaultMessage: "Meetings"
            })}
          </strong>
        </Typography>
        <NoData data={meetingsByDay}>
          <Grid container spacing={1}>
            {orderBy(Object.keys(meetingsByDay), day => moment(day, "DD/MM/YYYY"), "asc").map(
              day => (
                <>
                  <Grid item xs={12} sm={3} md={2} key={day}>
                    <Typography variant="h6">{day}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={9} md={10}>
                    <List>
                      {orderBy(meetingsByDay[day], ({ meetingSlot }) => meetingSlot.startAt).map(
                        meeting => {
                          return (
                            <MeetingListItem
                              meetingSlot={meeting?.meetingSlot}
                              meeting={meeting}
                              profile={
                                user?.profile?.id === meeting?.profile?.id
                                  ? meeting?.meetingSlot?.profile
                                  : meeting?.profile
                              }
                              selected={false}
                              onDelete={onDeleteMeeting}
                            />
                          );
                        }
                      )}
                    </List>
                  </Grid>
                </>
              )
            )}
          </Grid>
        </NoData>
      </Loader>
    </BaseLayout>
  );
};

export default AgendaPage;
